import { createAction, props } from "@ngrx/store";
import { IAskForQuotation, INewQuotation, IQuotation, IQuotationDetail, IQuotationFilter, IQuotationFilterRequest, IQuotationsKPIs, IQuotationSourceContent, ISentToErpRespone, IUpdateEmailOrder, IUpdateAiNotes, IUpdateQuotationDetails,  IQuotationAvailabilityReq, checkQuotationAvailabilityResponse, IDeleteDuplicateProducts, IPromptToOrder, IUpdateQuotationLinesPayload, IOrderLineAlert, IQuotationAlertsResponse, IHistory, IQuotationDealsResponse, IUpdateLinesDelta, IDocumentCoordinatesResponse} from "../models/quotations.models";
import { IOrderUser } from "src/app/features/orders/models/order.model";
import { IQuotePageCount } from "src/app/shared/models/page.model";
import { APIResponse } from "src/app/core/models/api-response";
import { AppError } from "src/app/core/models/api-error";

// get quotations
export const GetQuotations = createAction(
  '[ AskForQuotation ] - GetQuotations',
    props<{ page: IQuotePageCount; filter?: IQuotationFilterRequest | undefined; key?: string | undefined; sort_by?: string; sort_mode?: string, status?: string, archived?: number}>()
);
export const GetQuotationsSuccess = createAction(
  '[ AskForQuotation ] - GetQuotations Success ',
    props<{ res: APIResponse<IQuotationsKPIs>  }>()
);
export const GetQuotationsError = createAction(
  '[ AskForQuotation ] - GetQuotations Error',
);

// inline owner change on the list (no refetch — avoids read-replica lag)
export const SetQuotationOwner = createAction(
  '[ AskForQuotation ] - SetQuotationOwner',
  props<{ id: number; owner: IOrderUser }>()
);

export const SetQuotationFilter = createAction(
  '[ Filter ] - SetQuotationFilter',
  props<{ keyword: string, advanced: IQuotationFilter | null }>()
)

// processing placeholder visibility
export const ToggleProcessingVisibility = createAction(
  '[ Quotations ] - ToggleProcessingVisibility'
);
export const SetProcessingVisibility = createAction(
  '[ Quotations ] - SetProcessingVisibility',
  props<{ visible: boolean }>()
);
export const RefreshQuotations = createAction(
  '[ Quotations ] - RefreshQuotations'
);

export const SetQuotationSearch = createAction(
  '[ Filter ] - SetIQuotationSearch',
  props<{ search: string }>()
)

// get quotation by id
export const GetQuotationById = createAction(
  '[ Quotation ] - GetQuotationById',
  props<{ id: number; skipGlobalLoader?: boolean; paginate_lines?: boolean; page?: number; limit?: number; append?: boolean; include_ai_details?: boolean }>()
);
export const GetQuotationByIdSuccess = createAction(
  '[ Quotation ] - GetQuotationById Success ',
  props<{ quotation: IQuotationDetail; append?: boolean }>()
);
export const GetQuotationByIdError = createAction(
  '[ Quotation ] - GetQuotationById Error',
);

// append next page of quotation lines
export const AppendQuotationPage = createAction(
  '[ Quotation ] - AppendQuotationPage',
  props<{ id: number }>()
);

// send quotation
export const SendQuotation = createAction(
  '[ SendQuotation ] - SendQuotation',
  props<{ id: number, accept?: boolean }>()
);
export const SendQuotationSuccess = createAction(
  '[ SendQuotation ] - SendQuotation Success ',
  props<{ quotation: IQuotation }>()
);
export const SendQuotationError = createAction(
  '[ SendQuotation ] - SendQuotation Error',
);

// approve quotation
export const validateOrder = createAction(
  '[ validateOrder ] - validateOrder',
  props<{ id: number, shouldDelete?: boolean, force?: boolean, forceSend?: boolean }>()
)
export const validateOrderSuccess = createAction(
  '[ validateOrder ] - validateOrder Success ',
  // `quotation` is the ERP response, not the order — it carries no id. `orderId`
  // is the validated order, so a surface rendering several orders at once (the
  // Smart Inbox artifact) can tell whether this one concerns it.
  props<{ quotation: ISentToErpRespone, orderId: number }>()
)
export const validateOrderError = createAction(
  '[ validateOrder ] - validateOrder Error',
  // The ERP's own rejection wording is the actionable part of this failure
  // It must survive the effect, not be swallowed by catchError.
  props<{ error: AppError }>()
)

// Send new quotation
export const SendNewQuotation = createAction(
  '[ SendNewQuotation ] - SendNewQuotation',
  props<{ newQuotation: INewQuotation }>()
);
export const SendNewQuotationSuccess = createAction(
  '[ SendNewQuotation ] - SendNewQuotation Success ',
  props<{ quotation: IQuotationDetail }>()
);
export const SendNewQuotationError = createAction(
  '[ SendNewQuotation ] - SendNewQuotation Error',
);

// resend quotation
export const ResendQuotation = createAction(
  '[ ResendQuotation ] - ResendQuotation',
  props<{ id: number }>()
);
export const ResendQuotationSuccess = createAction(
  '[ ResendQuotation ] - ResendQuotation Success ',
);
export const ResendQuotationError = createAction(
  '[ ResendQuotation ] - ResendQuotation Error',
);

// accept quotation
export const AcceptQuotation = createAction(
  '[ AcceptQuotation ] - AcceptQuotation',
  props<{ id: number }>()
);
export const AcceptQuotationSuccess = createAction(
  '[ AcceptQuotation ] - AcceptQuotation Success ',
  props<{ quotation: IQuotationDetail }>()
);
export const AcceptQuotationError = createAction(
  '[ AcceptQuotation ] - AcceptQuotation Error',
);
// draft new quotation
export const DraftNewQuotation = createAction(
  '[ DraftNewQuotation ] - DraftNewQuotation',
  props<{ newQuotation: INewQuotation }>()
);
export const DraftNewQuotationSuccess = createAction(
  '[ DraftNewQuotation ] - DraftNewQuotation Success ',
  props<{ quotation: IQuotationDetail }>()
);
export const DraftNewQuotationError = createAction(
  '[ DraftNewQuotation ] - DraftNewQuotation Error',
);

// edit draft quotation
export const EditDraftQuotation = createAction(
  '[ EditDraftQuotation ] - EditDraftQuotation',
  props<{ id: number, quotation: IAskForQuotation }>()
);
export const EditDraftQuotationSuccess = createAction(
  '[ EditDraftQuotation ] - EditDraftQuotation Success ',
  props<{ quotation: IQuotationDetail }>()
);
export const EditDraftQuotationError = createAction(
  '[ EditDraftQuotation ] - EditDraftQuotation Error',
);

// edit quotation
export const EditQuotation = createAction(
  '[ EditQuotation ] - EditQuotation',
  props<{ id: number, quotation: IAskForQuotation, update_status?: string }>()
);
export const EditQuotationSuccess = createAction(
  '[ EditQuotation ] - EditQuotation Success ',
  props<{ quotation: IQuotationDetail }>()
);
export const EditQuotationError = createAction(
  '[ EditQuotation ] - EditQuotation Error',
);

// edit and send quotation
export const EditAndSendQuotation = createAction(
  '[ EditAndSendQuotation ] - EditAndSendQuotation',
  props<{ id: number, quotation: IAskForQuotation }>()
);
export const EditAndSendQuotationSuccess = createAction(
  '[ EditAndSendQuotation ] - EditAndSendQuotation Success ',
  props<{ quotation: IQuotationDetail }>()
);
export const EditAndSendQuotationError = createAction(
  '[ EditAndSendQuotation ] - EditAndSendQuotation Error',
);

// update quotation details
export const UpdateQuotationDetails = createAction(
  '[ UpdateQuotationDetails ] - UpdateQuotationDetails',
  props<{ id: number, payload: IUpdateQuotationDetails }>()
);
export const UpdateQuotationDetailsSuccess = createAction(
  '[ UpdateQuotationDetails ] - UpdateQuotationDetails Success ',
  // `changes` is a Partial, so its `id` is not guaranteed by the type; `orderId`
  // is (see validateOrderSuccess).
  props<{ changes: Partial<IQuotationDetail>, orderId: number }>()
);
export const UpdateQuotationDetailsError = createAction(
  '[ UpdateQuotationDetails ] - UpdateQuotationDetails Error',
  // `orderId` lets a caller that dispatched UpdateQuotationDetails for a specific
  // order (see applySuggestedLocationThenValidate) tell its own failure apart
  // from an unrelated concurrent request against a different order.
  props<{ orderId: number }>(),
);

// dismiss AI suggested shipping location
export const DismissSuggestedLocation = createAction(
  '[ DismissSuggestedLocation ] - DismissSuggestedLocation',
  props<{ id: number }>()
);
export const DismissSuggestedLocationSuccess = createAction(
  '[ DismissSuggestedLocation ] - DismissSuggestedLocation Success ',
  props<{ id: number }>()
);
export const DismissSuggestedLocationError = createAction(
  '[ DismissSuggestedLocation ] - DismissSuggestedLocation Error',
);

// duplicate badge dismissal
export const DismissDuplicateBadge = createAction(
  '[ Quotation ] - DismissDuplicateBadge',
  props<{ id: number }>()
);
export const DismissDuplicateBadgeSuccess = createAction(
  '[ Quotation ] - DismissDuplicateBadge Success',
  props<{ id: number }>()
);
export const DismissDuplicateBadgeError = createAction(
  '[ Quotation ] - DismissDuplicateBadge Error'
);

// update quotation lines
export const UpdateQuotationLines = createAction(
  '[ UpdateQuotationLines ] - UpdateQuotationLines',
  props<{ id: number, payload: IUpdateQuotationLinesPayload }>()
);
export const UpdateQuotationLinesSuccess = createAction(
  '[ UpdateQuotationLines ] - UpdateQuotationLines Success ',
  // `delta` describes the lines, not the order — it carries no id (see
  // validateOrderSuccess).
  props<{ delta: IUpdateLinesDelta, orderId: number }>()
);
export const UpdateQuotationLinesError = createAction(
  '[ UpdateQuotationLines ] - UpdateQuotationLines Error',
);

// cancel quotation
export const cancelQuotation = createAction(
  '[ cancelQuotation ] - cancelQuotation',
  props<{ id: number }>()
);
export const cancelQuotationSuccess = createAction(
  '[ cancelQuotation ] - cancelQuotation Success ',
  props<{ quotation: IQuotationDetail }>()
);
export const cancelQuotationError = createAction(
  '[ cancelQuotation ] - cancelQuotation Error',
);

// delete quotation
export const deleteQuotation = createAction(
  '[ deleteQuotation ] - deleteQuotation',
  props<{ id: number, confirm?: boolean }>()
);
export const deleteQuotationSuccess = createAction(
  '[ deleteQuotation ] - deleteQuotation Success ',
  props<{ id: number }>()
);
export const deleteQuotationError = createAction(
  '[ deleteQuotation ] - deleteQuotation Error',
);

// duplicate quotation
export const DuplicateQuotation = createAction(
  '[ DuplicateQuotation ] - DuplicateQuotation',
  props<{ id: number }>()
);
export const DuplicateQuotationSuccess = createAction(
  '[ DuplicateQuotation ] - DuplicateQuotation Success ',
  // `quotation` is the NEW order; `sourceId` is the one it was duplicated from.
  // A surface listing the source (the Smart Inbox artifact) has to match on
  // `sourceId` — the new order's id appears nowhere in what it renders yet.
  props<{ quotation: IQuotationDetail, sourceId: number }>()
);
export const DuplicateQuotationError = createAction(
  '[ DuplicateQuotation ] - DuplicateQuotation Error',
);

// bulk duplicate quotations (no navigation, refreshes list)
export const BulkDuplicateQuotations = createAction(
  '[ BulkDuplicateQuotations ] - BulkDuplicateQuotations',
  props<{ ids: number[] }>()
);
export const BulkDuplicateQuotationsSuccess = createAction(
  '[ BulkDuplicateQuotations ] - BulkDuplicateQuotations Success',
);
export const BulkDuplicateQuotationsError = createAction(
  '[ BulkDuplicateQuotations ] - BulkDuplicateQuotations Error',
);

// bulk archive quotations
export const BulkArchiveQuotations = createAction(
  '[ BulkArchiveQuotations ] - BulkArchiveQuotations',
  props<{ ids: number[] }>()
);
export const BulkArchiveQuotationsSuccess = createAction(
  '[ BulkArchiveQuotations ] - BulkArchiveQuotations Success',
);
export const BulkArchiveQuotationsError = createAction(
  '[ BulkArchiveQuotations ] - BulkArchiveQuotations Error',
);

// bulk unarchive quotations
export const BulkUnarchiveQuotations = createAction(
  '[ BulkUnarchiveQuotations ] - BulkUnarchiveQuotations',
  props<{ ids: number[] }>()
);
export const BulkUnarchiveQuotationsSuccess = createAction(
  '[ BulkUnarchiveQuotations ] - BulkUnarchiveQuotations Success',
);
export const BulkUnarchiveQuotationsError = createAction(
  '[ BulkUnarchiveQuotations ] - BulkUnarchiveQuotations Error',
);

// bulk delete quotations
export const BulkDeleteQuotations = createAction(
  '[ BulkDeleteQuotations ] - BulkDeleteQuotations',
  props<{ ids: number[] }>()
);
export const BulkDeleteQuotationsSuccess = createAction(
  '[ BulkDeleteQuotations ] - BulkDeleteQuotations Success',
);
export const BulkDeleteQuotationsError = createAction(
  '[ BulkDeleteQuotations ] - BulkDeleteQuotations Error',
);

// archive quotation
export const ArchiveQuotation = createAction(
  '[ ArchiveQuotation ] - ArchiveQuotation',
  props<{ id: number; quotationName?: string }>()
);
export const ArchiveQuotationSuccess = createAction(
  '[ ArchiveQuotation ] - ArchiveQuotation Success ',
  props<{ id: number; quotation?: IQuotationDetail; quotationName?: string }>()
);
export const ArchiveQuotationError = createAction(
  '[ ArchiveQuotation ] - ArchiveQuotation Error',
);

// unarchive (restore) quotation
export const UnarchiveQuotation = createAction(
  '[ UnarchiveQuotation ] - UnarchiveQuotation',
  props<{ id: number }>()
);
export const UnarchiveQuotationSuccess = createAction(
  '[ UnarchiveQuotation ] - UnarchiveQuotation Success ',
  props<{ id: number; quotation?: IQuotationDetail }>()
);
export const UnarchiveQuotationError = createAction(
  '[ UnarchiveQuotation ] - UnarchiveQuotation Error',
);

// invoice quotation
export const invoiceQuotation = createAction(
  '[ invoiceQuotation ] - invoiceQuotation',
  props<{ id: number }>()
);
export const invoiceQuotationSuccess = createAction(
  '[ invoiceQuotation ] - invoiceQuotation Success ',
  props<{ quotation: IQuotationDetail }>()
);
export const invoiceQuotationError = createAction(
  '[ invoiceQuotation ] - invoiceQuotation Error',
);

// Is Copilot
export const SetIsCopilot = createAction(
  '[ Quotation ] - SetIsCopilot',
  props<{ isCopilot: boolean }>()
);



// Order Status
export const UpdateOrderStatus = createAction(
  '[ UpdateOrderStatus ] - UpdateOrderStatus',
    props<{id: number, status: string, parentId: number }>()
);
export const UpdateOrderStatusSuccess = createAction(
  '[ UpdateOrderStatus ] - UpdateOrderStatus Success '
);
export const UpdateOrderStatusError = createAction(
  '[ UpdateOrderStatus ] - UpdateOrderStatus Error',
);

// Payment Status
export const UpdatePaymentStatus = createAction(
  '[ UpdatePaymentStatus ] - UpdatePaymentStatus',
    props<{id: number, status: string, parentId: number }>()
);
export const UpdatePaymentStatusSuccess = createAction(
  '[ UpdatePaymentStatus ] - UpdatePaymentStatus Success '
);
export const UpdatePaymentStatusError = createAction(
  '[ UpdatePaymentStatus ] - UpdatePaymentStatus Error',
);

// looder actions
export const SetQuotationsLoader = createAction(
  '[ Quotation ] - SetLoading',
  props<{ isLoading: boolean }>()
);

export const SetPromptToOrderLoader = createAction(
  '[ Quotation ] - SetPromptToOrderLoading',
  props<{ isLoading: boolean }>()
);
export const clearQuotations = createAction(
  '[ Quotations ] - ClearQuotations',
);
export function SetSearchQuotation(SetSearchQuotation: any, arg1: (state: import("./quotations.reducers").quotationsState, action: any) => { searchQuotation: any; quotations: IQuotation[]; kpi: import("../models/quotations.models").IQuotationKPI | undefined; quotation: IQuotationDetail | undefined; metadata: import("src/app/shared/models/page.model").IPagination | null; quotationFilter: IQuotationFilter; isCopilot: boolean; loading: boolean; }): import("@ngrx/store").ReducerTypes<import("./quotations.reducers").quotationsState, readonly import("@ngrx/store").ActionCreator[]> {
  throw new Error("Function not implemented.");
}

// export
export const exportQuotation = createAction(
  '[Quotation] Export Quotation',
  props<{filter?: IQuotationFilterRequest | undefined; key?: string | undefined; co_pilot: number}>()
);
export const exportQuotationSuccess = createAction(
  '[Quotation] Export Quotation Success',
  props<{ file: Blob }>()
);
export const exportQuotationFailure = createAction(
  '[Quotation] Export Quotation Failure',
  props<{ error: any }>()
);

// get quotation source content
export const GetQuotationSourceContent = createAction(
  '[Quotation] Get Quotation Source Content',
  props<{ id: number; skipGlobalLoader?: boolean }>()
);
export const GetQuotationSourceContentSuccess = createAction(
  '[Quotation] Get Quotation Source Content Success',
  props<{ quotationId: number; quotation: IQuotationSourceContent }>()
);
export const GetQuotationSourceContentError = createAction(
  '[Quotation] Get Quotation Source Content Error',
);

// get quotation document coordinates (WB-6815) — served by a dedicated endpoint, not inline on ai_details
export const GetQuotationDocumentCoordinates = createAction(
  '[Quotation] Get Quotation Document Coordinates',
  props<{ id: number; skipGlobalLoader?: boolean }>()
);
export const GetQuotationDocumentCoordinatesSuccess = createAction(
  '[Quotation] Get Quotation Document Coordinates Success',
  props<{ quotationId: number; coordinates: IDocumentCoordinatesResponse }>()
);
export const GetQuotationDocumentCoordinatesError = createAction(
  '[Quotation] Get Quotation Document Coordinates Error',
);

// clear quotation source content
export const ClearQuotationSourceContent = createAction(
  '[Quotation] Clear Quotation Source Content',
);

// update email order
export const UpdateEmailOrder = createAction(
  '[Quotation] Update Email Order',
  props<{ id: number, payload: IUpdateEmailOrder }>()
);
export const UpdateEmailOrderSuccess = createAction(
  '[Quotation] Update Email Order Success',
  props<{ quotation: IQuotationDetail }>()
);
export const UpdateEmailOrderError = createAction(
  '[Quotation] Update Email Order Error',
);

// update ai notes
export const UpdateAiNotes = createAction(
  '[Quotation] Update AI Notes',
  props<{ id: number, payload: IUpdateAiNotes }>()
);
export const UpdateAiNotesSuccess = createAction(
  '[Quotation] Update AI Notes Success',
  props<{ quotation: IQuotationDetail }>()
);
export const UpdateAiNotesError = createAction(
  '[Quotation] Update AI Notes Error',
);

// prompt to order
export const PromptToOrder = createAction(
  '[ Quotation ] - PromptToOrder',
  props<{ id: number, payload: IPromptToOrder }>()
);
export const PromptToOrderSuccess = createAction(
  '[ Quotation ] - PromptToOrder Success',
  props<{ quotation: IQuotationDetail }>()
);
export const PromptToOrderError = createAction(
  '[ Quotation ] - PromptToOrder Error',
);

// check products availability
export const CheckProductsAvailability = createAction(
  '[ Quotation ] - CheckProductsAvailability',
  props<{ data: IQuotationAvailabilityReq }>()
);
export const CheckProductsAvailabilitySuccess = createAction(
  '[ Quotation ] - CheckProductsAvailability Success',
  props<{ data: checkQuotationAvailabilityResponse }>()
);
export const CheckProductsAvailabilityError = createAction(
  '[ Quotation ] - CheckProductsAvailability Error',
);

// delete duplicate products
export const DeleteDuplicateProducts = createAction(
  '[ Quotation ] - DeleteDuplicateProducts',
  props<{ id: number, payload: IDeleteDuplicateProducts }>()
);
export const DeleteDuplicateProductsSuccess = createAction(
  '[ Quotation ] - DeleteDuplicateProducts Success',
  props<{ quotation: IQuotationDetail }>()
);
export const DeleteDuplicateProductsError = createAction(
  '[ Quotation ] - DeleteDuplicateProducts Error',
);

// toggle order line alert (ignore/revert)
export const ToggleOrderLineAlert = createAction(
  '[ Quotation ] - ToggleOrderLineAlert',
  props<{ quotationId: number; orderLineId: number; alertId: number }>()
);
export const ToggleOrderLineAlertSuccess = createAction(
  '[ Quotation ] - ToggleOrderLineAlert Success',
  props<{ orderLineId: number; alert: IOrderLineAlert }>()
);
export const ToggleOrderLineAlertError = createAction(
  '[ Quotation ] - ToggleOrderLineAlert Error',
);

// ignore all open alerts for a quotation
export const IgnoreAllAlerts = createAction(
  '[ Quotation ] - IgnoreAllAlerts',
  props<{ quotationId: number; alertIds: number[] }>()
);
export const IgnoreAllAlertsSuccess = createAction(
  '[ Quotation ] - IgnoreAllAlerts Success',
  props<{ alertIds: number[] }>()
);
export const IgnoreAllAlertsError = createAction(
  '[ Quotation ] - IgnoreAllAlerts Error',
);

// load quotation alerts (dedicated endpoint)
export const LoadQuotationAlerts = createAction(
  '[ Quotation ] - LoadQuotationAlerts',
  props<{ quotationId: number }>()
);
export const LoadQuotationAlertsSuccess = createAction(
  '[ Quotation ] - LoadQuotationAlerts Success',
  props<{ alertsResponse: IQuotationAlertsResponse }>()
);
export const LoadQuotationAlertsError = createAction(
  '[ Quotation ] - LoadQuotationAlerts Error',
);

// lazy-load full quotation history
export const LoadQuotationHistory = createAction(
  '[ Quotation ] - LoadQuotationHistory',
  props<{ quotationId: number }>()
);
export const LoadQuotationHistorySuccess = createAction(
  '[ Quotation ] - LoadQuotationHistory Success',
  props<{ history: IHistory[] }>()
);
export const LoadQuotationHistoryError = createAction(
  '[ Quotation ] - LoadQuotationHistory Error',
);

// mark an order as read (clears the unread indicator when opened)
export const MarkQuotationRead = createAction(
  '[ Quotation ] - MarkQuotationRead',
  props<{ id: number }>()
);

// lazy-load full quotation deals
export const LoadQuotationDeals = createAction(
  '[ Quotation ] - LoadQuotationDeals',
  props<{ quotationId: number }>()
);
export const LoadQuotationDealsSuccess = createAction(
  '[ Quotation ] - LoadQuotationDeals Success',
  props<{ dealsResponse: IQuotationDealsResponse }>()
);
export const LoadQuotationDealsError = createAction(
  '[ Quotation ] - LoadQuotationDeals Error',
);
